import React, { useState, useEffect, useCallback } from 'react';
// React Router - 既存プロジェクトで利用済み
// Material-UI components - 既存プロジェクトの@mui/materialを使用
// Material-UI icons - 既存プロジェクトの@mui/iconsを使用
// axios - HTTP client（既存プロジェクトで利用済み）
import Header from '../../components/Header';

const ClientProductSelect = () => {
  // 状態管理
  const [activeStep, setActiveStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // データ取得（既存のAnalysisHomeから移植）
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch('/api/clients/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setClients(data || []);
    } catch (err) {
      setError('顧客データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch('/api/products', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      const activeProducts = (data || []).filter(p => p.active !== false);
      setProducts(activeProducts);
    } catch (err) {
      setError('製品データの取得に失敗しました');
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, [fetchClients, fetchProducts]);

  // ステップ進行処理
  const handleNext = () => {
    if (activeStep === 0 && selectedClient) {
      setActiveStep(1);
    } else if (activeStep === 1 && selectedProduct) {
      handleAnalysisStart();
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  // 分析開始処理
  const handleAnalysisStart = () => {
    if (selectedClient && selectedProduct) {
      const params = new URLSearchParams({
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        productId: selectedProduct.id,
        productName: selectedProduct.name
      });
      // 既存の詳細分析画面へ遷移
      window.location.href = `/analysis/client-product?${params}`;
    }
  };

  // 顧客選択処理
  const handleClientSelect = (event, newValue) => {
    setSelectedClient(newValue);
    if (newValue && activeStep === 0) {
      setTimeout(() => setActiveStep(1), 300);
    }
  };

  // 製品選択処理
  const handleProductSelect = (event, newValue) => {
    setSelectedProduct(newValue);
  };

  const steps = [
    {
      label: '顧客を選択',
      description: '分析対象の顧客を選択してください'
    },
    {
      label: '製品を選択',
      description: '分析対象の製品を選択してください'
    }
  ];

  return (
    <>
      {/* Header component */}
      <div style={{ 
        maxWidth: '768px', 
        margin: '0 auto', 
        padding: '24px',
        paddingBottom: '96px' // モバイル対応
      }}>
        {/* ヘッダーセクション */}
        <div style={{
          padding: '24px',
          marginBottom: '32px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <span style={{ fontSize: '32px' }}>📊</span>
            <h1 style={{ fontWeight: 600, margin: 0, fontSize: '28px' }}>
              顧客製品詳細分析
            </h1>
          </div>
          <p style={{ opacity: 0.9, margin: 0 }}>
            特定の顧客・製品組み合わせの詳細分析
          </p>
        </div>

        {/* エラー表示 */}
        {error && (
          <div style={{
            padding: '16px',
            marginBottom: '24px',
            backgroundColor: '#ffebee',
            border: '1px solid #f44336',
            borderRadius: '4px',
            color: '#d32f2f'
          }}>
            {error}
          </div>
        )}

        {/* メインコンテンツ */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '32px'
        }}>
          {/* ステッパー */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              {steps.map((step, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  flex: 1 
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: activeStep >= index ? '#667eea' : '#e0e0e0',
                    color: activeStep >= index ? 'white' : '#666',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }}>
                    {index + 1}
                  </div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 500 }}>
                    {step.label}
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666', textAlign: 'center' }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ステップコンテンツ */}
          <div style={{ marginTop: '32px' }}>
            {activeStep === 0 && (
              <div>
                <h3 style={{ marginBottom: '16px', fontWeight: 500 }}>
                  分析対象の顧客を選択
                </h3>
                <p style={{ color: '#666', marginBottom: '24px' }}>
                  供給データを分析したい顧客を選択してください
                </p>
                
                {/* 簡易オートコンプリート */}
                <select
                  value={selectedClient?.id || ''}
                  onChange={(e) => {
                    const client = clients.find(c => c.id === parseInt(e.target.value));
                    handleClientSelect(null, client);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    marginBottom: '24px'
                  }}
                  disabled={loading}
                >
                  <option value="">顧客を選択...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} {client.address && `- ${client.address}`}
                    </option>
                  ))}
                </select>

                {selectedClient && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e8f5e8',
                    border: '1px solid #4caf50',
                    borderRadius: '4px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{ fontSize: '24px' }}>🏢</span>
                      <div>
                        <p style={{ margin: 0, fontWeight: 500 }}>
                          選択中: {selectedClient.name}
                        </p>
                        {selectedClient.address && (
                          <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                            {selectedClient.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeStep === 1 && (
              <div>
                <h3 style={{ marginBottom: '16px', fontWeight: 500 }}>
                  分析対象の製品を選択
                </h3>
                <p style={{ color: '#666', marginBottom: '24px' }}>
                  {selectedClient?.name}の供給データを分析する製品を選択してください
                </p>

                {/* 製品選択 */}
                <select
                  value={selectedProduct?.id || ''}
                  onChange={(e) => {
                    const product = products.find(p => p.id === parseInt(e.target.value));
                    handleProductSelect(null, product);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    marginBottom: '24px'
                  }}
                >
                  <option value="">製品を選択...</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - 単位: {product.unit}
                    </option>
                  ))}
                </select>

                {selectedProduct && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '4px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{ fontSize: '24px' }}>📦</span>
                      <div>
                        <p style={{ margin: 0, fontWeight: 500 }}>
                          選択中: {selectedProduct.name}
                        </p>
                        <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
                          単位: {selectedProduct.unit}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 選択サマリー */}
          {(selectedClient || selectedProduct) && (
            <div style={{
              padding: '24px',
              marginTop: '32px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}>
              <h3 style={{ marginBottom: '16px', fontWeight: 500 }}>
                選択内容
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {selectedClient && (
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    borderRadius: '16px',
                    fontSize: '14px'
                  }}>
                    🏢 顧客: {selectedClient.name}
                  </span>
                )}
                {selectedProduct && (
                  <span style={{
                    padding: '4px 8px',
                    backgroundColor: '#2196f3',
                    color: 'white',
                    borderRadius: '16px',
                    fontSize: '14px'
                  }}>
                    📦 製品: {selectedProduct.name}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* アクションボタン */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '32px',
            gap: '16px'
          }}>
            <button
              disabled={activeStep === 0}
              onClick={handleBack}
              style={{
                padding: '12px 24px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                opacity: activeStep === 0 ? 0.5 : 1
              }}
            >
              戻る
            </button>
            
            <button
              onClick={handleNext}
              disabled={
                (activeStep === 0 && !selectedClient) ||
                (activeStep === 1 && !selectedProduct)
              }
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '4px',
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                color: 'white',
                cursor: ((activeStep === 0 && !selectedClient) || (activeStep === 1 && !selectedProduct)) ? 'not-allowed' : 'pointer',
                opacity: ((activeStep === 0 && !selectedClient) || (activeStep === 1 && !selectedProduct)) ? 0.5 : 1
              }}
            >
              {activeStep === 1 ? '📈 分析開始' : '次へ ➡️'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientProductSelect;